/**
 * Terminal 82 - Shared Layout Components
 *
 * Header and footer used across all pages.
 */

// Register <t82-auth-section> custom element
import '../studio/sections/auth.ts';

export interface HeaderOptions {
  showPredictButton?: boolean;
  navLinks?: Array<{ href: string; label: string; active?: boolean }>;
  rightContent?: string; // Custom HTML for right side (replaces nav)
}

export function renderHeader(options: HeaderOptions = {}): string {
  const { showPredictButton = false, navLinks = [], rightContent } = options;

  // Right side: nav links + auth (always separate containers)
  const authTag = '<t82-auth-section></t82-auth-section>';
  const panelClose = '<button class="panel-close" id="panelClose" aria-label="Close menu">\u2715</button>';
  const rightHtml = rightContent
    ? `<div class="header-right">${panelClose}<div class="header-auth">${authTag}</div>${rightContent}</div>`
    : `<div class="header-right">${panelClose}<div class="header-auth">${authTag}</div><nav class="header-nav">${navLinks.map((link) => `<a href="${link.href}" class="nav-link${link.active ? ' active' : ''}">${link.label}</a>`).join('')}</nav></div>`;

  const predictBarHtml = showPredictButton ? `
    <div class="predict-bar" id="predict-bar">
      <button class="predict-trigger" id="predict-trigger">PREDICT TOMORROW</button>
    </div>
  ` : '';

  return `
    <header class="builder-header">
      <div class="brand">
        <a href="/" class="logo-link">
          <h1 class="logo">TERMINAL <span class="logo-accent">82</span></h1>
        </a>
        <span class="tagline">One card per day. Minted at close.</span>
        <span class="header-countdown" id="headerCountdown">--:--:--</span>
      </div>
      <button class="menu-toggle" id="menuToggle" aria-label="Open menu">
        <span class="menu-bar"></span>
        <span class="menu-bar"></span>
        <span class="menu-bar"></span>
      </button>
      ${rightHtml}
    </header>
    <div class="menu-backdrop" id="menuBackdrop"></div>
    <div id="prediction-modal-container"></div>
    ${predictBarHtml}
  `;
}

export function renderFooter(): string {
  return `
    <footer class="builder-footer">
      <div class="footer-content">
        <span class="footer-brand">TERMINAL 82</span>
        <span class="footer-text">One card per day. Minted at close.</span>
      </div>
      <div class="footer-meta">
        <span>ETH since July 30, 2015</span>
      </div>
    </footer>
  `;
}

/**
 * Mobile slide-out menu — toggle, close, escape, resize.
 * Runs once on import; safe to call multiple times (idempotent via flag).
 */
let _menuBound = false;
export function initMobileMenu(): void {
  if (_menuBound) return;
  _menuBound = true;

  const BREAKPOINT = 640;

  function openPanel() {
    document.querySelector('.header-right')?.classList.add('open');
    document.getElementById('menuBackdrop')?.classList.add('open');
    document.body.style.overflow = 'hidden';
  }

  function closePanel() {
    document.querySelector('.header-right')?.classList.remove('open');
    document.getElementById('menuBackdrop')?.classList.remove('open');
    document.body.style.overflow = '';
  }

  // Event delegation on document — survives re-renders
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;

    // Hamburger open
    if (target.closest('#menuToggle')) {
      openPanel();
      return;
    }

    // Close button
    if (target.closest('#panelClose')) {
      closePanel();
      return;
    }

    // Backdrop
    if (target.id === 'menuBackdrop') {
      closePanel();
      return;
    }

    // Nav link inside panel (navigate & close)
    if (target.closest('.header-right .nav-link')) {
      closePanel();
    }
  });

  // Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closePanel();
  });

  // Auto-close when resizing past breakpoint
  window.addEventListener('resize', () => {
    if (window.innerWidth > BREAKPOINT) closePanel();
  });
}

/**
 * Tier Dots Loader - Animated circular loader with tier-colored dots
 * @param text - Optional loading text to display below the spinner
 */
export function renderLoader(text?: string): string {
  return `
    <div class="tier-dots-loader">
      <span class="loader-dot common"></span>
      <span class="loader-dot uncommon"></span>
      <span class="loader-dot rare"></span>
      <span class="loader-dot epic"></span>
      <span class="loader-dot legendary"></span>
      <span class="loader-dot mythic"></span>
    </div>
    ${text ? `<span class="loader-text">${text}</span>` : ''}
  `;
}

// Auto-init mobile menu on import (all pages import this module)
initMobileMenu();

// Auto-init header countdown on all pages
function initHeaderCountdown(): void {
  function update() {
    const el = document.getElementById('headerCountdown');
    if (!el) return;

    const now = new Date();
    const utcMidnight = new Date(Date.UTC(
      now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 0, 0
    ));
    const ms = utcMidnight.getTime() - now.getTime();
    const h = Math.floor(ms / (1000 * 60 * 60));
    const m = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    const s = Math.floor((ms % (1000 * 60)) / 1000);
    el.textContent = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }

  update();
  setInterval(update, 1000);
}

initHeaderCountdown();
